import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Card,
  Chip,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DownloadIcon from "@mui/icons-material/Download";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import AdMediaPlayer from "./AdMediaPlayer";
import useAdDownload from "./useAdDownload";
import { tokens } from "../../theme";

// Human-readable file size for the download button subtitle.
const formatBytes = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: tokens.warning, bg: tokens.warningSoft },
  approved: { label: "Approved", color: tokens.accent, bg: tokens.accentSoft },
  rejected: { label: "Rejected", color: tokens.danger, bg: tokens.dangerSoft },
  running: { label: "Running", color: tokens.success, bg: tokens.successSoft },
  expired: { label: "Expired", color: tokens.text3, bg: tokens.surface3 },
};

const SectionCard = ({ title, right, children }) => (
  <Card sx={{ mb: 1.75 }}>
    <Box sx={{ p: "14px 18px 0" }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>{title}</Typography>
        {right && <Box sx={{ ml: "auto" }}>{right}</Box>}
      </Stack>
    </Box>
    <Box sx={{ p: "12px 18px 18px" }}>{children}</Box>
  </Card>
);

SectionCard.propTypes = {
  title: PropTypes.node,
  right: PropTypes.node,
  children: PropTypes.node,
};

const AdvertisementDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const { download, downloadingId, error: downloadError, clearError } = useAdDownload();

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/advertisements/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setAd(await res.json());
        else navigate("/advertisements");
      } catch {
        navigate("/advertisements");
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
  }, [id, navigate]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: tokens.accent }} />
      </Box>
    );
  }
  if (!ad) return null;

  const statusCfg = STATUS_CONFIG[ad.approvalStatus] || STATUS_CONFIG.pending;

  const details = [
    { label: "Media Type", value: ad.mediaType === "video" ? "Video" : "Image" },
    { label: "Priority", value: `Level ${ad.priority || 1}` },
    {
      label: "Start Date",
      value: ad.startDate
        ? new Date(ad.startDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "—",
    },
    {
      label: "End Date",
      value: ad.endDate
        ? new Date(ad.endDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "—",
    },
    {
      label: "Uploaded",
      value: new Date(ad.createdAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    },
  ];

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        pt: { xs: 1.5, sm: 2 },
        pb: { xs: 2, sm: 3 },
        maxWidth: 900,
        width: "100%",
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/advertisements")}
        size="small"
        sx={{
          mb: 2,
          color: tokens.text2,
          "&:hover": { color: tokens.text, background: tokens.surface2 },
        }}
      >
        Back to advertisements
      </Button>

      {/* Media Preview */}
      <Card sx={{ mb: 1.75, overflow: "hidden" }}>
        <AdMediaPlayer ad={ad} />
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{ p: "12px 14px", borderTop: `1px solid ${tokens.border}` }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.text }}>
              {ad.originalFileName || "Original media file"}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: tokens.text3 }}>
              {[ad.mediaType === "video" ? "Video" : "Image", formatBytes(ad.fileSize)]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              downloadingId === ad._id ? (
                <CircularProgress size={13} sx={{ color: tokens.accent }} />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={downloadingId === ad._id}
            onClick={() => download(ad)}
            sx={{ ml: "auto", flexShrink: 0, borderColor: tokens.border, color: tokens.text }}
          >
            {downloadingId === ad._id ? "Preparing…" : "Download"}
          </Button>
        </Stack>
      </Card>

      {/* Info Card */}
      <SectionCard
        title={ad.advertisementName}
        right={
          <Chip
            label={statusCfg.label}
            size="small"
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              height: 24,
              background: statusCfg.bg,
              color: statusCfg.color,
            }}
          />
        }
      >
        <Typography sx={{ fontSize: 12.5, color: tokens.text3, mt: -1, mb: 1.5 }}>
          {ad.category || "Uncategorized"}
        </Typography>

        {ad.approvalStatus === "rejected" && ad.rejectionReason && (
          <Box sx={{ p: 1.5, borderRadius: "9px", background: tokens.dangerSoft, mb: 1.75 }}>
            <Typography sx={{ fontSize: 12, color: tokens.danger, fontWeight: 600 }}>
              Rejection reason: {ad.rejectionReason}
            </Typography>
          </Box>
        )}

        {ad.description && (
          <Typography sx={{ fontSize: 13, color: tokens.text2, lineHeight: 1.6, mb: 1.75 }}>
            {ad.description}
          </Typography>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.75 }}>
          {details.map(({ label, value }) => (
            <Box key={label}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: tokens.text3,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 0.25,
                }}
              >
                {label}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: tokens.text }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>

      {/* Assigned Robots */}
      <SectionCard title={`Assigned robots (${ad.assignedRobots?.length || 0})`}>
        {!ad.assignedRobots?.length ? (
          <Typography sx={{ fontSize: 13, color: tokens.text3 }}>
            No robots assigned to this advertisement
          </Typography>
        ) : (
          <Stack direction="row" gap={1.25} flexWrap="wrap">
            {ad.assignedRobots.map((r) => (
              <Box
                key={r._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.9,
                  borderRadius: "9px",
                  border: `1px solid ${tokens.border}`,
                  background: tokens.surface2,
                }}
              >
                <SmartToyIcon sx={{ fontSize: 15, color: tokens.text2 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.text }}>
                  {r.name}
                </Typography>
                <Chip
                  label={r.status}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 600,
                    background: r.status === "active" ? tokens.successSoft : tokens.surface3,
                    color: r.status === "active" ? tokens.success : tokens.text3,
                  }}
                />
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>

      <Snackbar
        open={!!downloadError}
        autoHideDuration={4000}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" sx={{ borderRadius: "10px" }}>
          {downloadError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdvertisementDetails;
